import React, { FC } from 'react';

import { useIntl } from 'react-intl';
import styled, { css } from 'styled-components';

import Checkbox from 'components/checkbox';
import { CountrySelectInput } from 'components/country-select-input';
import { FloatingLabelInput } from 'components/floating-label-fields';
import useCreditCardFormProps from 'hooks/form/use-credit-card-form-props';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { usePaymentContext } from 'state/payment';
import { CardType } from 'state/payment/types';
import { FormValidationState } from 'utils/form';
import { IAdyenPaymentState, IPaymentErrors, IPaymentState, excludeNumeric } from 'utils/payment';

import AdyenCustomCardInputs from './adyen-credit-card-form-inputs/adyen-credit-card-inputs';
import { IAdyenErrors, IAdyenState } from './adyen-credit-card-form-inputs/types';
import { BillingSameAsDeliveryLabel, SaveCardForFutureLabel } from './credit-card-form-labels';
import FormTitle from './credit-card-form-title';
import FirstDataCreditCardInputs from './first-data-credit-card-form-inputs/first-data-credit-card-inputs';
import { messages } from './messages';

interface IFormContainer {
  withBorder: boolean;
}

export const FormContainer = styled.div<IFormContainer>`
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
  background-color: transparent;
  font-size: 0.75rem;
  input[type='number']::-webkit-outer-spin-button,
  input[type='number']::-webkit-inner-spin-button {
    appearance: none;
    margin: 0;
  }

  input[type='number'] {
    -moz-appearance: textfield;
  }

  ${Styles.mobile`
    margin-top: 1rem;
  `}

  ${Styles.desktop`
    padding: 1rem;

    ${(p: IFormContainer) =>
      p.withBorder &&
      css`
        border: 1px solid ${Styles.color.grey.four};
        background-color: white;
      `}
    `}
`;

export const NameOnCardWrapper = styled.div`
  flex-basis: 100%;
`;

export const CountrySelectWrapper = styled.div`
  flex-basis: 100%;
`;

const StreetAddressWrapper = styled.div`
  flex-basis: 100%;
`;

const BillingAptWrapper = styled.div`
  flex-basis: 45%;
  flex-grow: 1;
  ${Styles.desktop`
    flex-basis: 19%;
    margin-right: 5%;
  `}
`;
const BillingZipWrapper = styled.div`
  flex-basis: 45%;
  flex-grow: 1;
  ${Styles.desktop`
    flex-basis: 20%;
  `}
`;

const BillingCityWrapper = styled.div`
  flex-basis: 60%;
  margin-right: 5%;
  ${Styles.desktop`
    flex-basis: 30%;
    margin-right: 5%;
  `}
`;

const BillingStateWrapper = styled.div`
  flex-basis: 35%;
  ${Styles.desktop`
    flex-basis: 16%;
    margin-right: 5%;
  `}
`;

interface ICreditCardFormInputs {
  errors: IPaymentErrors;
  isDelivery?: boolean;
  onChange: (name: string, value: string) => void;
  onAdyenChange?: (state: IAdyenState) => void;
  onAdyenError?: (errors: IAdyenErrors) => void;
  paymentValues: IPaymentState | IAdyenPaymentState;
  showSaveCard?: boolean;
  withBorder?: boolean;
  ariaLabel?: string;
  supportedCardTypes?: CardType[];
}

const CreditCardFormInputs: FC<ICreditCardFormInputs> = ({
  errors,
  isDelivery = false,
  onChange,
  onAdyenChange,
  onAdyenError,
  paymentValues,
  showSaveCard = true,
  withBorder = false,
  ariaLabel,
  supportedCardTypes = []
}) => {
  // retrieve additional credit card form props
  const { postalCodeInputProps, regionInputProps } = useCreditCardFormProps(
    paymentValues.billingCountry
  );
  const { formatMessage } = useIntl();
  const { isAdyen } = usePaymentContext();

  const handleChange: React.ChangeEventHandler<HTMLSelectElement | HTMLInputElement> = e =>
    onChange(e.target.name, e.target.value);

  // Hides Apt and State/Province field from Add New Payment Method form
  const shouldHideField = useFlag(LaunchDarklyFlag.ENABLE_HIDE_ADD_NEW_PAYMENT_FIELDS);

  return (
    <FormContainer withBorder={withBorder} aria-label={ariaLabel}>
      <FormTitle
        title={formatMessage(messages.pleaseEnterYourCreditCardInformation)}
        creditText={formatMessage(messages.credit)}
      />
      <NameOnCardWrapper>
        <FloatingLabelInput
          label={formatMessage(messages.nameOnCard)}
          name="nameOnCard"
          data-testid="nameOnCard"
          value={paymentValues.nameOnCard}
          onChange={handleChange}
          required
          autocomplete="cc-name"
          validation={errors.nameOnCard ? FormValidationState.INVALID : undefined}
          message={errors.nameOnCard}
        />
      </NameOnCardWrapper>
      {isAdyen ? (
        <AdyenCustomCardInputs onChange={onAdyenChange} onError={onAdyenError} />
      ) : (
        <FirstDataCreditCardInputs
          onChange={handleChange}
          supportedCardTypes={supportedCardTypes}
          paymentValues={paymentValues}
          errors={errors}
        />
      )}
      {isDelivery && (
        <Checkbox
          data-testid="billing-same-as-delivery"
          label={<BillingSameAsDeliveryLabel />}
          name="billingAddressSameAsDelivery"
          checked={paymentValues.billingAddressSameAsDelivery}
          onChange={handleChange}
        />
      )}
      <StreetAddressWrapper>
        <FloatingLabelInput
          label={formatMessage(messages.streetAddress)}
          name="billingStreetAddress"
          data-testid="billingStreetAddress"
          value={paymentValues.billingStreetAddress}
          onChange={handleChange}
          required
          autocomplete="billing street-address"
          validation={errors.billingStreetAddress ? FormValidationState.INVALID : undefined}
          message={errors.billingStreetAddress}
          readOnly={isDelivery && paymentValues.billingAddressSameAsDelivery}
        />
      </StreetAddressWrapper>
      {!shouldHideField && (
        <BillingAptWrapper>
          <FloatingLabelInput
            label={formatMessage(messages.aptOrSuite)}
            aria-label={formatMessage(messages.apartment)}
            name="billingApt"
            data-testid="billingApt"
            value={paymentValues.billingApt}
            onChange={handleChange}
            validation={errors.billingApt ? FormValidationState.INVALID : undefined}
            message={errors.billingApt}
            autocomplete="billing street-apt"
            readOnly={isDelivery && paymentValues.billingAddressSameAsDelivery}
          />
        </BillingAptWrapper>
      )}
      <BillingCityWrapper>
        <FloatingLabelInput
          label={formatMessage(messages.city)}
          name="billingCity"
          data-testid="billingCity"
          value={paymentValues.billingCity}
          onChange={handleChange}
          required
          type="text"
          autocomplete="billing locality"
          validation={errors.billingCity ? FormValidationState.INVALID : undefined}
          message={errors.billingCity}
          readOnly={isDelivery && paymentValues.billingAddressSameAsDelivery}
        />
      </BillingCityWrapper>
      {!shouldHideField && (
        <BillingStateWrapper>
          <FloatingLabelInput
            name="billingState"
            data-testid="billingState"
            value={excludeNumeric(paymentValues.billingState)}
            onChange={handleChange}
            required
            autocomplete="billing region"
            validation={errors.billingState ? FormValidationState.INVALID : undefined}
            message={errors.billingState}
            readOnly={isDelivery && paymentValues.billingAddressSameAsDelivery}
            {...regionInputProps}
          />
        </BillingStateWrapper>
      )}
      <BillingZipWrapper>
        {/* Additional props are spread: label, maxLength, pattern */}
        <FloatingLabelInput
          name="billingZip"
          data-testid="billingZip"
          value={paymentValues.billingZip}
          onChange={handleChange}
          required
          autocomplete="billing postal-code"
          validation={errors.billingZip ? FormValidationState.INVALID : undefined}
          message={errors.billingZip}
          readOnly={isDelivery && paymentValues.billingAddressSameAsDelivery}
          {...postalCodeInputProps}
        />
      </BillingZipWrapper>
      <CountrySelectWrapper>
        <CountrySelectInput
          label={formatMessage(messages.country)}
          name="billingCountry"
          data-testid="billingCountry"
          required
          value={paymentValues.billingCountry}
          onChange={handleChange}
          autocomplete="billing country"
          validation={errors.billingCountry ? FormValidationState.INVALID : undefined}
          message={errors.billingCountry}
          readOnly={!!(isDelivery && paymentValues.billingCountry)}
        />
      </CountrySelectWrapper>
      {showSaveCard && (
        <Checkbox
          label={<SaveCardForFutureLabel />}
          name="saveCard"
          data-testid="saveCard"
          checked={paymentValues.saveCard}
          onChange={handleChange}
        />
      )}
    </FormContainer>
  );
};

export default CreditCardFormInputs;
